import { Col, Grid, CardBody, TextField, Checkbox } from "capitalroadkit";
import React, { useEffect } from "react";
import { Field } from "formik";
import clone from "lodash/cloneDeep";
import { MenuItem } from "@material-ui/core";
import * as Yup from "yup";

import { parseEnum, toAPIDateString, toDateString } from "../../../../resources/formatters/Formatter";
import withNameSpace from "../../../../functions/withNameSpace";

import AssetType from "../../../../types/Assets/AssetType";
import CreateAssetType from "../../../../types/Assets/CreateAssetType";
import SecurityForm from "./SecurityForm";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import useQueryAPI from "../../../../hooks/useQueryAPI";
import { getRegistriesListAxios } from "../../api/ReferenceResource";

const fields = {
  ASSET_SOURCE: "assetSource",
  PROVIDER: "provider",
  TYPE: "type",
  NAME: "name",
  NAME_OVERRIDE: "nameOverride",
  ASSET_CODE: "assetCode",
  SCALE: "scale",
  CURRENCY: "currency",
  COUNTRY_OF_ISSUE: "countryOfIssue",
  LISTED: "listed",
  REGISTRY: "registry",
  AMIT: "amit",
  AMIT_URL: "amitUrl",
  SUSPENDED: "suspended",
  ACTIVE: "active",
  EXPIRY_DATE: "expiryDate"
};

const initialValues = (asset) => {
  const baseInitialValues = {
    [fields.ASSET_SOURCE]: asset?.assetSource || "SYSTEM",
    [fields.PROVIDER]: asset?.provider || "ADMIN",
    [fields.TYPE]: asset?.type || "",
    [fields.NAME]: asset?.name || "",
    [fields.NAME_OVERRIDE]: asset?.nameOverride || "",
    [fields.ASSET_CODE]: asset?.assetCode || "",
    [fields.CURRENCY]: asset?.currency || "",
    [fields.COUNTRY_OF_ISSUE]: asset?.countryOfIssue || "",
    [fields.LISTED]: asset?.listed || false,
    [fields.REGISTRY]: asset?.registryKey || "",
    [fields.AMIT]: asset?.amit || false,
    [fields.AMIT_URL]: asset?.amitUrl || "",
    [fields.SUSPENDED]: asset?.suspended || false,
    [fields.ACTIVE]: asset?.active || true,
    [fields.EXPIRY_DATE]: asset?.expiryDate ? toDateString(asset.expiryDate) : "",
  };

  switch (asset?.type) {
    case AssetType.EQUITY:
    case AssetType.EXCHANGE_TRADED_FUND:
    case AssetType.DEBT_SECURITY:
      return { ...baseInitialValues, ...SecurityForm.initialValues(asset) };

    default:
      return baseInitialValues;
  }
};

const schema = (asset) => {
  const optionalSchema = () => {
    switch (asset?.type) {
      case AssetType.EQUITY:
      case AssetType.EXCHANGE_TRADED_FUND:
      case AssetType.DEBT_SECURITY:
        return SecurityForm.schema.fields;

      default:
        return null;
    }
  };

  return Yup.object({
    [fields.CURRENCY]: Yup.string().matches(/^[A-Z]{3}$/, "Must provide a valid currency eg. AUD"),
    [fields.COUNTRY_OF_ISSUE]: Yup.string().matches(/^[A-Z]{2}$/, "Must provide a valid country of issue eg. AU"),
    ...optionalSchema()
  });
};

const clean = (values) => {
  let cleanValues = clone(values);

  switch (values[fields.TYPE]) {
    case AssetType.EQUITY:
    case AssetType.EXCHANGE_TRADED_FUND:
    case AssetType.DEBT_SECURITY:
      cleanValues = SecurityForm.clean(cleanValues);
      break;

    default:
      break;
  }

  const registry = cleanValues[fields.REGISTRY];
  if (registry === "") {
    cleanValues[fields.REGISTRY] = null;
  } else if (registry && typeof registry === "string") {
    cleanValues[fields.REGISTRY] = {
      bundle: "registry_provider",
      dataKey: registry,
    };
  }
  if (cleanValues[fields.EXPIRY_DATE])
    cleanValues[fields.EXPIRY_DATE] = toAPIDateString(cleanValues[fields.EXPIRY_DATE]);
  if (
    cleanValues[fields.TYPE] !== AssetType.EXCHANGE_TRADED_FUND &&
    cleanValues[fields.TYPE] !== AssetType.MANAGED_FUND &&
    cleanValues[fields.TYPE] !== AssetType.EQUITY &&
    cleanValues[fields.TYPE] !== AssetType.DEBT_SECURITY) {
    delete cleanValues[fields.AMIT];
    delete cleanValues[fields.AMIT_URL];
  }

  for (const propName in cleanValues) {
    if (cleanValues[propName] === "") {
      delete cleanValues[propName];
    }
  }
  return cleanValues;
};

const AssetForm = ({ form, nameSpace, asset, setSchema }) => {
  let type = null;
  if (asset?.type) type = asset?.type;
  else if (form.values?.type) type = form.values[fields.TYPE];
  else type = AssetType.EQUITY;

  useEffect(() => {
    if (setSchema && form.values?.type !== "") setSchema(schema({ type: form.values?.type }));
  }, [form.values?.type]);

  useEffect(() => {
    switch (type) {
      case AssetType.EQUITY:
      case AssetType.EXCHANGE_TRADED_FUND:
      case AssetType.DEBT_SECURITY:
      case AssetType.WARRANT:
        form.setValues({ ...initialValues(asset), type, ...SecurityForm.initialValues(asset) });
        break;

      default:
        break;
    }
  }, [type]);

  const renderAssetForm = () => {
    switch (type) {
      case AssetType.EQUITY:
      case AssetType.EXCHANGE_TRADED_FUND:
      case AssetType.DEBT_SECURITY:
        return (
          <SecurityForm
            form={form}
            nameSpace={nameSpace}
            asset={asset}
            listed={form.values[withNameSpace(nameSpace, fields.LISTED)]}
          />
        );

      default:
        return null;
    }
  };

  const { data: registries, isLoading: registriesLoading, isError: registriesError } =
    useQueryAPI(["referenceData", "registries"], getRegistriesListAxios);

  return (
    <Grid nested>
      <Col sm={12}>
        <CardBody nested>
          <Grid nested>
            <Col sm={12}>Base asset fields</Col>
            <Col sm={12}>
              <Field
                key={form.values[withNameSpace(nameSpace, fields.TYPE)]}
                component={TextField}
                label="Type"
                fullWidth
                name={withNameSpace(nameSpace, fields.TYPE)}
                readOnly={asset?.type}
                required
                select
              >
                {asset?.type && !Object.values(CreateAssetType).includes(asset?.type) ? (
                  // Editing mode with a type outside CreateAssetType (e.g. MANAGED_FUND)
                  <MenuItem value={asset?.type}>{parseEnum(asset?.type)}</MenuItem>
                ) : (
                  // Create mode (or editing one of the creatable types)
                  Object.values(CreateAssetType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {parseEnum(type)}
                    </MenuItem>
                  ))
                )}
              </Field>
            </Col>
            <Col sm={12}>
              <Field
                component={TextField}
                label="Name"
                fullWidth
                name={withNameSpace(nameSpace, fields.NAME)}
                readOnly={asset?.name}
                required
              />
            </Col>
            <Collapse in={asset?.name} style={{ width: "100%" }}>
              <Col sm={12}>
                <Tooltip title={preferredNameTooltip()} style={{ maxWidth: "none" }} placement="right-start">
                  <Field
                    component={TextField}
                    label="Name override"
                    fullWidth
                    name={withNameSpace(nameSpace, fields.NAME_OVERRIDE)}
                    required
                  />
                </Tooltip>
              </Col>
            </Collapse>
            <Collapse in={!form.values[withNameSpace(nameSpace, fields.LISTED)]} style={{ width: "100%" }}>
              <Col sm={12}>
                <Field
                  component={TextField}
                  label="Asset code"
                  fullWidth
                  name={withNameSpace(nameSpace, fields.ASSET_CODE)}
                  readOnly={asset?.assetCode}
                  required={!form.values[withNameSpace(nameSpace, fields.LISTED)]}
                />
              </Col>
            </Collapse>
            <Col sm={12}>
              <Field
                component={TextField}
                label="Currency"
                fullWidth
                name={withNameSpace(nameSpace, fields.CURRENCY)}
                readOnly={asset?.currency}
                required
                select
              >
                <MenuItem value="AUD">AUD</MenuItem>
              </Field>
            </Col>
            <Col sm={12}>
              <Field
                component={TextField}
                label="Country of issue"
                fullWidth
                name={withNameSpace(nameSpace, fields.COUNTRY_OF_ISSUE)}
                required
                select
              >
                <MenuItem value="AU">AU</MenuItem>
              </Field>
            </Col>
            <Col sm={12}>
              <Field
                component={Checkbox}
                label="Listed"
                fullWidth
                name={withNameSpace(nameSpace, fields.LISTED)}
                readOnly={asset}
              />

            </Col>
            <Col sm={12}>
              <Field
                component={TextField}
                label="Registry"
                fullWidth
                name={withNameSpace(nameSpace, fields.REGISTRY)}
                select
                disabled={registriesLoading || registriesError}
                helperText={
                  registriesError
                    ? "Failed to load registries"
                    : registriesLoading
                      ? "Loading registries..."
                      : " "
                }
              >
                <MenuItem value="" >
                  Choose a registry
                </MenuItem>
                {(registries || []).map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.name}
                  </MenuItem>
                ))}
              </Field>
            </Col>

            {(
              form.values[withNameSpace(nameSpace, fields.TYPE)] === AssetType.EXCHANGE_TRADED_FUND ||
              form.values[withNameSpace(nameSpace, fields.TYPE)] === AssetType.MANAGED_FUND ||
              form.values[withNameSpace(nameSpace, fields.TYPE)] === AssetType.EQUITY ||
              form.values[withNameSpace(nameSpace, fields.TYPE)] === AssetType.DEBT_SECURITY

            ) && (
              <Col sm={12}>
                <Field
                  component={Checkbox}
                  label="Attribution Managed Investment Trust- AMIT"
                  fullWidth
                  name={withNameSpace(nameSpace, fields.AMIT)}

                />
                <Collapse in={form.values[withNameSpace(nameSpace, fields.AMIT)]} style={{ width: "100%" }}>
                  <Col sm={12}>
                    <Field
                      component={TextField}
                      label="AMIT URL"
                      fullWidth
                      name={withNameSpace(nameSpace, fields.AMIT_URL)}
                      placeholder="Enter related URL"
                    />
                  </Col>
                </Collapse>
              </Col>
            )}
            <Col sm={12}>
              <Field
                component={Checkbox}
                label="Suspended"
                fullWidth
                name={withNameSpace(nameSpace, fields.SUSPENDED)}
              />
            </Col>
            <Col sm={12}>
              <Field component={Checkbox} label="Active" fullWidth name={withNameSpace(nameSpace, fields.ACTIVE)} />
            </Col>
            <Col sm={12}>
              <Field
                component={TextField}
                label="Expiry date"
                mask="99/99/9999"
                fullWidth
                name={withNameSpace(nameSpace, fields.EXPIRY_DATE)}
              />
            </Col>
          </Grid>
        </CardBody>
      </Col>
      {renderAssetForm()}
    </Grid>
  );
};

const preferredNameTooltip = () =>
  "The name override will be used to override the value in the asset name field. " +
  "You can remove it and it will revert back to the original.";

AssetForm.fields = fields;
AssetForm.initialValues = initialValues;
AssetForm.schema = schema;
AssetForm.clean = clean;

export default AssetForm;
